import { useEffect, useState } from 'react';
import styles from './Support.module.css';
import { authService } from '../../services/auth.service';
import { UserById } from '../../services/getUserById.service';
import {
  supportService,
  SupportTicket,
  SupportTicketComment,
  SupportTicketFilters,
  SupportTicketPageInfo,
} from '../../services/support.service';
import {
  getAllEnvironmentsService,
  Environment,
} from '../../services/getAllEnvironment.service';

const PAGE_SIZE = 20;
const STATUS_OPTIONS = ['OPEN', 'IN_PROGRESS',  'CLOSED'];
const PRIORITY_OPTIONS = ['LOW', 'MEDIUM', 'HIGH', 'URGENT'];
const CATEGORY_OPTIONS = [
  { value: 'GENERAL', label: 'General' },
  { value: 'AUTHENTICATION', label: 'Authentication' },
  { value: 'PAYMENT', label: 'Payment' },
  { value: 'DEPOSIT', label: 'Deposit' },
  { value: 'WITHDRAWAL', label: 'Withdrawal' },
  { value: 'BONUS', label: 'Bonus' },
  { value: 'GAMES', label: 'Games' },
  { value: 'ACCOUNT', label: 'Account' },
  { value: 'TECHNICAL', label: 'Technical' },
  { value: 'OTHER', label: 'Other' },
];

const formatDate = (dateStr: string) =>
  new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const statusClass = (status: string): string => {
  switch (status?.toUpperCase()) {
    case 'OPEN': return styles.statusOpen;
    case 'IN_PROGRESS': return styles.statusInProgress;
    case 'RESOLVED': return styles.statusResolved;
    case 'CLOSED': return styles.statusClosed;
    default: return styles.statusDefault;
  }
};

const priorityClass = (priority: string): string => {
  switch (priority?.toUpperCase()) {
    case 'LOW': return styles.priorityLow;
    case 'MEDIUM': return styles.priorityMedium;
    case 'HIGH': return styles.priorityHigh;
    case 'URGENT': return styles.priorityUrgent;
    default: return styles.priorityDefault;
  }
};

const Support = () => {
  const currentUser = authService.getStoredUser();
  const isAdmin = currentUser?.role?.name === 'ADMIN';
  const userEnvironmentId = (currentUser as any)?.environment?.id as number | undefined;

  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pagination
  const [page, setPage] = useState(1);
  const [pageInfo, setPageInfo] = useState<SupportTicketPageInfo>({
    hasNextPage: false,
    total: 0,
  });

  // Admin environment filter
  const [environments, setEnvironments] = useState<Environment[]>([]);
  const [selectedEnvId, setSelectedEnvId] = useState<number | ''>('');

  // Filters
  const [filterStatus, setFilterStatus] = useState<string>('OPEN');
  const [filterPriority, setFilterPriority] = useState<string>('');
  const [filterCategory, setFilterCategory] = useState<string>('');
  const [filterUsername, setFilterUsername] = useState<string>('');

  // Detail panel
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Edit form (status + priority)
  const [editStatus, setEditStatus] = useState('');
  const [editPriority, setEditPriority] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Comment form
  const [commentText, setCommentText] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);

  useEffect(() => {
    if (isAdmin) {
      getAllEnvironmentsService.getAllEnvironments().then(setEnvironments).catch(console.error);
    }
  }, [isAdmin]);

  const fetchTickets = async () => {
    setLoading(true);
    setError(null);
    try {
      const envId = isAdmin
        ? (selectedEnvId ? Number(selectedEnvId) : undefined)
        : userEnvironmentId;
      const filters: SupportTicketFilters = {};
      if (filterStatus) filters.status = filterStatus;
      if (filterPriority) filters.priority = filterPriority;
      if (filterCategory) filters.category = filterCategory;
      if (filterUsername.trim()) filters.username = filterUsername.trim();
      const data = await supportService.getTicketsForEnvironment(envId, filters, {
        first: PAGE_SIZE,
        after: (page - 1) * PAGE_SIZE,
      });
      setTickets(data.tickets);
      setPageInfo(data.pageInfo);
    } catch (err: any) {
      setError(err?.message || 'Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEnvId, filterStatus, filterPriority, filterCategory, page]);

  // Manual search (username) — go back to first page, or refetch if already there
  const handleSearch = () => {
    if (page !== 1) setPage(1);
    else fetchTickets();
  };

  const handleUsernameSearch = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const openTicket = async (ticket: SupportTicket) => {
    setDetailLoading(true);
    setSelectedTicket(ticket);
    setEditStatus(ticket.status);
    setEditPriority(ticket.priority);
    setSaveSuccess(false);
    setCommentText('');
    try {
      const full = await supportService.getTicket(ticket.id);
      setSelectedTicket(full);
      setEditStatus(full.status);
      setEditPriority(full.priority);
    } catch (err) {
      console.error('Failed to load ticket details', err);
    } finally {
      setDetailLoading(false);
    }
  };

  const closePanel = () => {
    setSelectedTicket(null);
    setSaveSuccess(false);
    setCommentText('');
  };

  const handleSave = async () => {
    if (!selectedTicket) return;
    setSaving(true);
    setSaveSuccess(false);
    try {
      await supportService.updateTicket({
        id: selectedTicket.id,
        status: editStatus,
        priority: editPriority,
      });
      setSelectedTicket((prev) => prev ? { ...prev, status: editStatus, priority: editPriority } : prev);
      setTickets((prev) =>
        prev.map((t) => t.id === selectedTicket.id ? { ...t, status: editStatus, priority: editPriority } : t)
      );
      setSaveSuccess(true);
    } catch (err: any) {
      alert(err?.message || 'Failed to update ticket');
    } finally {
      setSaving(false);
    }
  };

  const handleAddComment = async () => {
    if (!selectedTicket || !commentText.trim()) return;
    setSubmittingComment(true);
    try {
      const newComment = await supportService.addComment({
        ticketId: selectedTicket.id,
        content: commentText.trim(),
      });
      setSelectedTicket((prev) =>
        prev
          ? { ...prev, comments: [...(prev.comments || []), newComment] }
          : prev
      );
      setCommentText('');
    } catch (err: any) {
      alert(err?.message || 'Failed to add comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  const isOpen = Boolean(selectedTicket);

  return (
    <main className={styles.main}>
      {/* Overlay */}
      <div
        className={`${styles.overlay} ${isOpen ? styles.open : ''}`}
        onClick={closePanel}
      />

      {/* Header */}
      <div className={styles.headerRow}>
        <h1 className={styles.title}>Support Tickets</h1>
        {isAdmin && environments.length > 0 && (
          <select
            className={styles.formSelect}
            value={selectedEnvId}
            onChange={(e) => {
              setSelectedEnvId(e.target.value ? Number(e.target.value) : '');
              setPage(1);
            }}
          >
            <option value="">All Environments</option>
            {environments.map((env) => (
              <option key={env.id} value={env.id}>
                {env.name} — {env.host}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Filters */}
      <div className={styles.filtersRow}>
        <select
          className={styles.filterSelect}
          value={filterStatus}
          onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}
        >
          <option value="">All Statuses</option>
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>{s.replace('_', ' ')}</option>
          ))}
        </select>
        <select
          className={styles.filterSelect}
          value={filterPriority}
          onChange={(e) => { setFilterPriority(e.target.value); setPage(1); }}
        >
          <option value="">All Priorities</option>
          {PRIORITY_OPTIONS.map((p) => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>
        <select
          className={styles.filterSelect}
          value={filterCategory}
          onChange={(e) => { setFilterCategory(e.target.value); setPage(1); }}
        >
          <option value="">All Categories</option>
          {CATEGORY_OPTIONS.map((c) => (
            <option key={c.value} value={c.value}>{c.label}</option>
          ))}
        </select>
        <input
          className={styles.filterInput}
          type="text"
          placeholder="Username..."
          value={filterUsername}
          onChange={(e) => setFilterUsername(e.target.value)}
          onKeyDown={handleUsernameSearch}
        />
        <button className={styles.filterBtn} onClick={handleSearch}>Search</button>
        <button
          className={styles.filterClearBtn}
          onClick={() => {
            setFilterStatus('');
            setFilterPriority('');
            setFilterCategory('');
            setFilterUsername('');
            setPage(1);
          }}
        >
          Clear
        </button>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {loading ? (
        <div className={styles.loading}>Loading tickets...</div>
      ) : tickets.length === 0 ? (
        <div className={styles.noData}>No support tickets found</div>
      ) : (
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>#</th>
                <th>Title</th>
                <th>Category</th>
                <th>Status</th>
                <th>Priority</th>
                <th>Reporter</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {tickets.map((ticket) => (
                <tr
                  key={ticket.id}
                  onClick={() => openTicket(ticket)}
                  className={selectedTicket?.id === ticket.id ? styles.selectedRow : ''}
                >
                  <td>{ticket.id}</td>
                  <td>{ticket.title}</td>
                  <td>{ticket.category}</td>
                  <td>
                    <span className={`${styles.badge} ${statusClass(ticket.status)}`}>
                      {ticket.status?.replace('_', ' ')}
                    </span>
                  </td>
                  <td>
                    <span className={`${styles.badge} ${priorityClass(ticket.priority)}`}>
                      {ticket.priority}
                    </span>
                  </td>
                  <td>{ticket.user?.username}</td>
                  <td>{formatDate(ticket.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && pageInfo.total > 0 && (
        <div className={styles.pagination}>
          <span className={styles.paginationInfo}>
            {(page - 1) * PAGE_SIZE + 1}–
            {Math.min(page * PAGE_SIZE, pageInfo.total)} of {pageInfo.total}
          </span>
          <div className={styles.paginationControls}>
            <button
              className={styles.pageBtn}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              Previous
            </button>
            <span className={styles.pageIndicator}>Page {page}</span>
            <button
              className={styles.pageBtn}
              onClick={() => setPage((p) => p + 1)}
              disabled={!pageInfo.hasNextPage}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Detail Panel */}
      <div className={`${styles.detailPanel} ${isOpen ? styles.open : ''}`}>
        {selectedTicket && (
          <>
            <div className={styles.detailPanelHeader}>
              <div className={styles.detailTitle}>#{selectedTicket.id} — {selectedTicket.title}</div>
              <button className={styles.closeBtn} onClick={closePanel}>×</button>
            </div>

            <div className={styles.detailBody}>
              {detailLoading ? (
                <div className={styles.loading}>Loading details...</div>
              ) : (
                <>
                  {/* Info */}
                  <div className={styles.detailSection}>
                    <div className={styles.detailSectionTitle}>Details</div>
                    <div className={styles.infoGrid}>
                      <div className={styles.infoItem}>
                        <span className={styles.infoLabel}>Category</span>
                        <span className={styles.infoValue}>{selectedTicket.category}</span>
                      </div>
                      <div className={styles.infoItem}>
                        <span className={styles.infoLabel}>Environment</span>
                        <span className={styles.infoValue}>{selectedTicket.environment?.name}</span>
                      </div>
                      <div className={styles.infoItem}>
                        <span className={styles.infoLabel}>Reporter</span>
                        <span className={styles.infoValue}>{selectedTicket.user?.username}</span>
                      </div>
                      <div className={styles.infoItem}>
                        <span className={styles.infoLabel}>Created</span>
                        <span className={styles.infoValue}>{formatDate(selectedTicket.createdAt)}</span>
                      </div>
                      {selectedTicket.dueDate && (
                        <div className={styles.infoItem}>
                          <span className={styles.infoLabel}>Due Date</span>
                          <span className={styles.infoValue}>{formatDate(selectedTicket.dueDate)}</span>
                        </div>
                      )}
                    </div>
                    {selectedTicket.description && (
                      <div className={styles.descriptionBox}>{selectedTicket.description}</div>
                    )}
                  </div>

                  {/* Update Status / Priority */}
                  <div className={styles.detailSection}>
                    <div className={styles.detailSectionTitle}>Update Ticket</div>
                    <div className={styles.editRow}>
                      <div className={styles.editGroup}>
                        <label className={styles.editLabel}>Status</label>
                        <select
                          className={styles.editSelect}
                          value={editStatus}
                          onChange={(e) => { setEditStatus(e.target.value); setSaveSuccess(false); }}
                        >
                          {STATUS_OPTIONS.map((s) => (
                            <option key={s} value={s}>{s.replace('_', ' ')}</option>
                          ))}
                        </select>
                      </div>
                      <div className={styles.editGroup}>
                        <label className={styles.editLabel}>Priority</label>
                        <select
                          className={styles.editSelect}
                          value={editPriority}
                          onChange={(e) => { setEditPriority(e.target.value); setSaveSuccess(false); }}
                        >
                          {PRIORITY_OPTIONS.map((p) => (
                            <option key={p} value={p}>{p}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    {saveSuccess && (
                      <div className={styles.successMsg}>Ticket updated successfully</div>
                    )}
                    <button
                      className={styles.saveBtn}
                      onClick={handleSave}
                      disabled={saving}
                    >
                      {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>

                  {/* Comments */}
                  <div className={styles.detailSection}>
                    <div className={styles.detailSectionTitle}>
                      Comments ({selectedTicket.comments?.length ?? 0})
                    </div>
                    <div className={styles.commentList}>
                      {(selectedTicket.comments ?? []).length === 0 ? (
                        <div className={styles.noComments}>No comments yet</div>
                      ) : (
                        (selectedTicket.comments as SupportTicketComment[]).map((c) => (
                          <div key={c.id} className={styles.commentItem}>
                            <div className={styles.commentMeta}>
                              <span className={styles.commentAuthor}>{c.user?.username}</span>
                              <span className={styles.commentDate}>{formatDate(c.createdAt)}</span>
                            </div>
                            <div className={styles.commentContent}>{c.content}</div>
                          </div>
                        ))
                      )}
                    </div>

                    <div className={styles.addCommentForm}>
                      <textarea
                        className={styles.commentTextarea}
                        placeholder="Write a comment..."
                        rows={3}
                        value={commentText}
                        onChange={(e) => setCommentText(e.target.value)}
                      />
                      <button
                        className={styles.submitCommentBtn}
                        onClick={handleAddComment}
                        disabled={submittingComment || !commentText.trim()}
                      >
                        {submittingComment ? 'Posting...' : 'Add Comment'}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </main>
  );
};

export default Support;
